// import React from 'react';
// import { motion } from 'motion/react';
// import { Check, Star } from 'lucide-react';
// import { useNavigate } from 'react-router-dom';

// interface PricingCardProps {
//   title: string;
//   price: string;
//   priceDetail?: string;
//   description: string;
//   features?: string[];
//   isRecommended?: boolean;
//   planId: string;
//   index: number;
// }

// const PricingCard: React.FC<PricingCardProps> = ({
//   title,
//   price,
//   priceDetail,
//   description,
//   features,
//   isRecommended,
//   planId,
//   index,
// }) => {
//   const navigate = useNavigate();

//   const handleCardClick = () => {
//     navigate(`/checkout?plan=${planId}`);
//   };

//   return (
//     <motion.div
//       initial={{ opacity: 0, y: 30 }}
//       whileInView={{ opacity: 1, y: 0 }}
//       viewport={{ once: true }}
//       transition={{ duration: 0.5, delay: index * 0.1 }}
//       whileHover={{ 
//         y: -8,
//         transition: { duration: 0.2 }
//       }}
//       onClick={handleCardClick}
//       className={`
//         relative cursor-pointer group
//         bg-card/50 backdrop-blur-sm
//         border border-border
//         rounded-2xl p-8 
//         transition-all duration-200
//         hover:shadow-2xl hover:shadow-accent/10
//         ${isRecommended ? 'ring-2 ring-accent shadow-xl shadow-accent/20' : 'shadow-lg'}
//       `}
//     >
//       {/* Recommended Badge */}
//       {isRecommended && (
//         <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-accent to-secondary rounded-full flex items-center gap-1.5 shadow-lg">
//           <Star className="w-3.5 h-3.5 text-white fill-white" />
//           <span className="text-white text-sm font-semibold">Best Value</span>
//         </div>
//       )}

//       {/* Card Content */}
//       <div className="text-center">
//         {/* Title */}
//         <h3 className="text-2xl font-semibold text-foreground mb-3">
//           {title}
//         </h3>

//         {/* Price */}
//         <div className="mb-4">
//           <div className="flex items-baseline justify-center gap-1">
//             <span className="text-5xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
//               {price}
//             </span>
//           </div>
//           {priceDetail && (
//             <p className="text-muted-foreground text-sm mt-1">{priceDetail}</p>
//           )}
//         </div>

//         {/* Description */}
//         <p className="text-muted-foreground mb-6">
//           {description}
//         </p>

//         {/* Features List (if provided) */}
//         {features && features.length > 0 && (
//           <ul className="space-y-3 mb-6 text-left">
//             {features.map((feature, idx) => (
//               <li key={idx} className="flex items-start gap-2">
//                 <Check className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
//                 <span className="text-foreground/80">{feature}</span>
//               </li>
//             ))}
//           </ul>
//         )}

//         {/* CTA Button */}
//         <button
//           className={`
//             w-full py-3.5 px-6 rounded-xl
//             font-semibold
//             transition-all duration-200
//             ${
//               isRecommended
//                 ? 'bg-gradient-to-r from-accent to-secondary text-white hover:shadow-lg hover:shadow-accent/30'
//                 : 'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/30'
//             }
//             group-hover:scale-[1.02]
//           `}
//           onClick={(e) => {
//             e.stopPropagation();
//             handleCardClick();
//           }}
//         >
//           Buy Now
//         </button>
//       </div>

//       {/* Glassmorphism Overlay on Hover */}
//       <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary/5 to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none" />
//     </motion.div>
//   );
// };

// export const Pricing: React.FC = () => {
//   const pricingPlans = [
//     {
//       title: 'One Card',
//       price: '₹2,000',
//       description: 'Perfect for single occasion greetings.',
//       features: [
//         'Single NFC Business Card',
//         'Customizable Design',
//         'QR Code Integration',
//         'Social Media Links',
//         'Lifetime Access',
//       ],
//       planId: 'one-card',
//       isRecommended: false,
//     },
//     {
//       title: 'Five Cards',
//       price: '₹1,500',
//       priceDetail: 'per card',
//       description: 'Ideal for multiple themed greetings.',
//       features: [
//         'Five NFC Business Cards',
//         'All Premium Themes',
//         'Priority Support',
//         'Bulk Discount Applied',
//         'Team Collaboration',
//       ],
//       planId: 'five-cards',
//       isRecommended: true,
//     },
//   ];

//   return (
//     <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
//       {/* Background Gradient Effects */}
//       <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-20 pointer-events-none" />
//       <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/10 rounded-full blur-3xl opacity-20 pointer-events-none" />

//       <div className="max-w-7xl mx-auto relative z-10">
//         {/* Section Header */}
//         <motion.div
//           initial={{ opacity: 0, y: 20 }}
//           whileInView={{ opacity: 1, y: 0 }}
//           viewport={{ once: true }}
//           transition={{ duration: 0.6 }}
//           className="text-center mb-16"
//         >
//           <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
//             Simple, Transparent Pricing
//           </h2>
//           <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
//             Choose the perfect plan for your needs. No hidden fees, no surprises.
//           </p>
//         </motion.div>

//         {/* Pricing Cards Grid */}
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
//           {pricingPlans.map((plan, index) => (
//             <PricingCard key={plan.planId} {...plan} index={index} />
//           ))}
//         </div>

//         {/* Additional Info */}
//         <motion.div
//           initial={{ opacity: 0 }}
//           whileInView={{ opacity: 1 }}
//           viewport={{ once: true }}
//           transition={{ duration: 0.6, delay: 0.3 }}
//           className="text-center mt-12"
//         >
//           <p className="text-muted-foreground text-sm">
//             All plans include free updates and customer support.
//           </p>
//         </motion.div>
//       </div>
//     </section>
//   );
// };



import React from "react";
import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { useNavigate } from "react-router-dom";

/* =========================
   Pricing Card Component
========================= */
const PricingCard = ({
  title,
  price,
  priceDetail,
  description,
  features,
  isRecommended,
  planId,
  index,
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/checkout?plan=${planId}`);
  };

  const cardStyle = {
    position: "relative",
    cursor: "pointer",
    borderRadius: "20px",
    padding: "32px",
    background: "#ffffff",
    border: isRecommended ? "2px solid #7c3aed" : "1px solid #e5e7eb",
    boxShadow: isRecommended
      ? "0 20px 40px rgba(124, 58, 237, 0.2)"
      : "0 10px 25px rgba(0,0,0,0.08)",
    transition: "all 0.2s ease",
  };

  const buttonStyle = {
    width: "100%",
    padding: "14px",
    borderRadius: "12px",
    border: "none",
    fontWeight: "600",
    fontSize: "16px",
    cursor: "pointer",
    background: isRecommended
      ? "linear-gradient(90deg,#7c3aed,#ec4899)"
      : "#111827",
    color: "#ffffff",
    marginTop: "10px",
  };

  return (
    <motion.div
    id="pricing"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -8 }}
      // onClick={handleCardClick}
      style={cardStyle}
    >
      {/* Recommended Badge */}
      {isRecommended && (
        <div
          style={{
            position: "absolute",
            top: "-16px",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "6px 16px",
            borderRadius: "999px",
            background: "linear-gradient(90deg,#7c3aed,#ec4899)",
            color: "#fff",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            boxShadow: "0 5px 15px rgba(0,0,0,0.15)",
          }}
        >
          <Star size={14} fill="#fff" />
          Best Value
        </div>
      )}

      <div style={{ textAlign: "center" }}>
        {/* Title */}
        <h3 style={{ fontSize: "24px", marginBottom: "12px" }}>
          {title}
        </h3>

        {/* Price */}
        <div style={{ marginBottom: "10px" }}>
          <span
            style={{
              fontSize: "48px",
              fontWeight: "700",
              background: "linear-gradient(90deg,#7c3aed,#ec4899)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {price}
          </span>
          {priceDetail && (
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              {priceDetail}
            </div>
          )}
        </div>

        {/* Description */}
        <p style={{ color: "#6b7280", marginBottom: "20px" }}>
          {description}
        </p>

        {/* Features */}
        {features && (
          <ul style={{ textAlign: "left", marginBottom: "20px" }}>
            {features.map((feature, idx) => (
              <li
                key={idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  gap: "8px",
                }}
              >
                <Check size={18} color="#7c3aed" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Button */}
        {/* <button
          style={buttonStyle}
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
        >
          Buy Now
        </button> */}
      </div>
    </motion.div>
  );
};

/* =========================
   Pricing Section
========================= */
const Pricing = () => {
  const pricingPlans = [
    {
      title: "One Card",
      price: "₹2,000",
      description: "Perfect for single occasion greetings.",
      features: [
        "Single NFC Business Card",
        "Customizable Design",
        "QR Code Integration",
        "Social Media Links",
      ],
      planId: "one-card",
      isRecommended: false,
    },
    {
      title: "Five Cards",
      price: "₹1,500",
      priceDetail: "per card",
      description: "Ideal for multiple themed greetings.",
      features: [
        "Five NFC Business Cards",
        "All Premium Themes",
        "Priority Support",
        "Bulk Discount Applied",
        "Team Collaboration",
      ],
      planId: "five-cards",
      isRecommended: true,
    },
  ];

  return (
    <section
      style={{
        padding: "80px 20px",
        background: "#f9fafb",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "60px" }}>
          <h2 style={{ fontSize: "42px", fontWeight: "700" }}>
            Simple, Transparent Pricing
          </h2>
          <p style={{ color: "#6b7280", marginTop: "10px" }}>
            Choose the perfect plan for your needs.
          </p>
        </div>

        {/* Cards Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "40px",
          }}
        >
          {pricingPlans.map((plan, index) => (
            <PricingCard key={plan.planId} {...plan} index={index} />
          ))}
        </div>

        {/* Footer */}
        <div style={{ textAlign: "center", marginTop: "50px" }}>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>
            All plans include free updates and customer support.
          </p>
        </div>
      </div>
    </section>
  );
};

export default Pricing;

